import { Box, Grid, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { ReactComponent as ApplePodcastIcon } from '../../../assets/images/apple_podcast.svg';
import { ReactComponent as GooglePodcastIcon } from '../../../assets/images/google_podcast.svg';
import { ReactComponent as RssFeedIcon } from '../../../assets/images/rss_feed.svg';
import { ReactComponent as SpotifyIcon } from '../../../assets/images/spotify.svg';
import { ReactComponent as WebIcon } from '../../../assets/images/web_podcast.svg';
import {
  ContentFeedSection,
  ContentInteractionAction,
  ContentPodcast, MetaItem, PodcastChannelName, PodcastDestination,
} from '../../../common/models/content/Content';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { useNavigationUtils } from '../../../common/utils/useNavigationUtils';

interface PodcastListenSubscribeSheetProps {
  contentId: number
  contentCreatorName?: string | null
  contentTitle?: string
  podcast?: ContentPodcast
  title?: string | null,
  heading?: string,
  contentMetaData?: MetaItem[],
  contentFeedSection?: ContentFeedSection,
  contentFeedSectionPosition?: number,
}

export default function PodcastListenSubscribeSheet(
  props: PodcastListenSubscribeSheetProps,
) {
  const theme = useTheme();
  const { trackContentInteraction } = useCustomEventTracking();
  const { openExternalLink } = useNavigationUtils();

  const firePodcastInteractionEvent = (channel: string, options?: {
    [k: string]: unknown;
  }) => {
    trackContentInteraction(
      ContentInteractionAction.Open,
      props.title?.replace(/^\w/, (c) => c.toUpperCase()) || '',
      {
        contentId: props.contentId,
        contentTitle: props.heading || '',
        contentCreatorName: props.contentCreatorName || '',
        contentMetaData: props.contentMetaData || [],
        type: props.title || '',
        channel: PodcastChannelName[channel as keyof typeof PodcastChannelName],
        podcastTitle: props.contentTitle || '',
        ...(props.contentFeedSection && { contentFeedSection: props.contentFeedSection }),
        ...(props.contentFeedSectionPosition &&
          { contentFeedSectionPosition: props.contentFeedSectionPosition }),
        ...options,
      },
    );
  };

  const openPodcast = (destination: PodcastDestination) => {
    firePodcastInteractionEvent(destination.platform);
    openExternalLink(destination.url);
  };

  const podcastDestinations = useMemo(() => {
    const customOrder = ['web', 'spotify', 'apple'];
    const destinations = props.podcast?.destinations || [];
    const sorted = [...destinations].sort(
      (a, b) => customOrder.indexOf(a.platform) - customOrder.indexOf(b.platform),
    );
    return sorted.slice(0, 2);
  }, [props.podcast?.destinations]);

  const platforms = {
    web: {
      name: PodcastChannelName.web,
      logo: () => <WebIcon width={30} height={30} />,
      button: () => (
        <img
          src="https://d2o7bp9oz3gksv.cloudfront.net/podcast_web_button_f5fb57b9eb.svg"
          alt="Listen on Web"
          style={{ width: '100%', height: 'auto' }}
        />
      ),
    },
    spotify: {
      name: PodcastChannelName.spotify,
      logo: () => <SpotifyIcon width={30} height={30} />,
      button: () => (
        <img
          src="https://d2o7bp9oz3gksv.cloudfront.net/podcast_spotify_button_8a1db05eed.svg"
          alt="Listen on Spotify"
          style={{ width: '100%', height: 'auto' }}
        />
      ),
    },
    apple: {
      name: PodcastChannelName.apple,
      logo: () => <ApplePodcastIcon width={30} height={30} />,
      button: () => (
        <img
          src="https://d2o7bp9oz3gksv.cloudfront.net/podcast_apple_button_d20085c402.svg"
          alt="Listen on Apple"
          style={{ width: '100%', height: 'auto' }}
        />
      ),
    },
    google: {
      name: PodcastChannelName.google,
      logo: () => <GooglePodcastIcon width={30} height={30} />,
      button: () => (
        <img
          src="https://d2o7bp9oz3gksv.cloudfront.net/podcast_google_button_e3940e585f.svg"
          alt="Listen on Google"
          style={{ width: '100%', height: 'auto' }}
        />
      ),
    },
    rss: {
      name: PodcastChannelName.rss,
      logo: () => <RssFeedIcon width={30} height={30} />,
      button: () => (
        <img
          src="https://d2o7bp9oz3gksv.cloudfront.net/podcast_RSS_button_e8d204ac3c.svg"
          alt="RSS Feed"
          style={{ width: '100%', height: 'auto' }}
        />
      ),
    },
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        wrap="nowrap"
      >
        {props.podcast && podcastDestinations && podcastDestinations
          .filter((item) => item.platform.toLowerCase() !== 'google')
          .map((item, index) => {
            const platform = platforms[item.platform as keyof typeof platforms] || platforms.web;
            return (
              <Grid
                item
                xs={6}
                key={`platform-list-${item.platform}`}
                id={`platform-${item.platform}`}
                sx={{
                  paddingTop: `${theme.spacing(2)} !important`,
                  paddingLeft: `${theme.spacing(2)} !important`,
                }}
              >
                <Box
                  sx={{
                    cursor: 'pointer',
                    '& svg': {
                      width: '100%',
                      height: 'auto',
                    },
                  }}
                  onClick={(e) => { e.stopPropagation(); openPodcast(item); }}
                >
                  {platform.button()}
                </Box>
              </Grid>
            );
          })}
      </Grid>
    </>
  );
}
